import React, { createContext, useContext, useState, useEffect } from 'react';

const UserContext = createContext();

export function useUser() {
  return useContext(UserContext);
}

// Restore user from localStorage on init (optimistic — validated against the
// server on mount via /api/access/me so a stale/expired token can't linger).
function getStoredUser() {
  try {
    const stored = localStorage.getItem('user');
    return stored || null;
  } catch {
    return null;
  }
}

function clearStoredAuth() {
  try {
    localStorage.removeItem('tkn');
    localStorage.removeItem('user');
  } catch { /* ignore */ }
}

export function UserProvider({ children }) {
  const [user, setUser] = useState(getStoredUser);

  const login = (userData) => {
    const userStr = typeof userData === 'string' ? userData : JSON.stringify(userData);
    localStorage.setItem('user', userStr);
    setUser(userStr);
  };

  const logout = (opts = {}) => {
    setUser(null);
    clearStoredAuth();
    // opts.reason surfaces a message on the login page (e.g. 'expired')
    const q = opts && opts.reason ? `?reason=${encodeURIComponent(opts.reason)}` : '';
    window.location.href = `/login${q}`;
  };

  // On mount: if a cached token exists, validate it against the server ONCE.
  // The Google ID token expires after ~1h and GIS does not auto-refresh; the
  // client guard previously only checked that a token STRING existed, which is
  // not real protection. /api/access/me re-verifies server-side (signature +
  // exp + aud). A 401/404 means the token is expired/invalid/user-removed →
  // drop the stale session and force re-login. Closes findings B and E.
  useEffect(() => {
    let cancelled = false;
    let tkn = null;
    try { tkn = localStorage.getItem('tkn'); } catch { tkn = null; }
    if (!tkn) return undefined;

    fetch('/api/access/me', { headers: { Authorization: `Bearer ${tkn}` } })
      .then((r) => {
        if (cancelled) return;
        if (r.status === 401 || r.status === 404) {
          clearStoredAuth();
          setUser(null);
          const path = window.location.pathname;
          // Only bounce if we're on a protected page (not already on login/waiting).
          if (!/^\/(login|waiting)/.test(path)) {
            window.location.href = '/login?reason=expired';
          }
        }
        // 200 → token still valid; leave the optimistically-restored user as-is
        // (pages needing fresh role/access re-fetch /api/access/me themselves).
      })
      .catch(() => { /* transient network error — do not nuke the session */ });

    return () => { cancelled = true; };
  }, []);

  return (
    <UserContext.Provider value={{ user, login, logout }}>
      {children}
    </UserContext.Provider>
  );
}
